import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Egg, TrendingUp, Calendar } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

interface EggProductionStats {
  totalEggs: number;
  averagePerDay: number;
  lastProduction: string | null;
}

interface EggProductionStatsProps {
  stockItemId: string;
}

export const EggProductionStats = ({ stockItemId }: EggProductionStatsProps) => {
  const fetchStats = async () => {
    console.log("Fetching stats for stockItemId:", stockItemId);
    const { data, error } = await supabase
      .from('egg_productions')
      .select('quantity, production_date')
      .eq('stock_item_id', stockItemId);

    if (error) {
      console.error("Error fetching stats:", error);
      throw error;
    }

    console.log("Fetched stats data:", data);

    if (!data || data.length === 0) {
      return {
        totalEggs: 0,
        averagePerDay: 0,
        lastProduction: null,
      };
    }

    const total = data.reduce((sum, record) => sum + record.quantity, 0);
    const dates = data.map(record => new Date(record.production_date));
    const lastDate = dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : null;
    const average = dates.length > 0 ? total / dates.length : 0;

    return {
      totalEggs: total,
      averagePerDay: Math.round(average),
      lastProduction: lastDate ? lastDate.toISOString() : null,
    };
  };

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['egg-production-stats', stockItemId],
    queryFn: fetchStats,
    enabled: !!stockItemId,
  });

  if (isLoading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card>
        <CardContent className="flex items-center p-6">
          <div>Chargement...</div>
        </CardContent>
      </Card>
    </div>;
  }

  if (error) {
    console.error("Error in EggProductionStats:", error);
    return <div className="text-red-500">Erreur lors du chargement des statistiques</div>;
  }

  const displayStats = stats || {
    totalEggs: 0,
    averagePerDay: 0,
    lastProduction: null,
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card>
        <CardContent className="flex items-center p-6">
          <Egg className="h-8 w-8 text-primary mr-4" />
          <div>
            <p className="text-sm text-muted-foreground">Total des œufs</p>
            <h3 className="text-2xl font-bold">{displayStats.totalEggs}</h3>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex items-center p-6">
          <TrendingUp className="h-8 w-8 text-primary mr-4" />
          <div>
            <p className="text-sm text-muted-foreground">Moyenne par jour</p>
            <h3 className="text-2xl font-bold">{displayStats.averagePerDay}</h3>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex items-center p-6">
          <Calendar className="h-8 w-8 text-primary mr-4" />
          <div>
            <p className="text-sm text-muted-foreground">Dernière production</p>
            <h3 className="text-lg font-bold">
              {displayStats.lastProduction 
                ? new Date(displayStats.lastProduction).toLocaleDateString()
                : "Aucune"}
            </h3>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};